import { AccountGroup } from '@amzn/aac-chrome-internal-api';
import { request } from 'src/commons/utils/async-request';

/**
 * Response for `POST /trim/accounts` — the whale account-switcher pagination
 * endpoint. The same endpoint serves browse (no query) and search (query
 * present); ACVS models both as one operation. `nextToken` is an opaque
 * pagination token echoed verbatim into the next request; `null` means there
 * are no more pages.
 */
export interface AccountPageResponse {
  accounts: AccountGroup[];
  totalCount: number;
  nextToken: string | null;
}

const ACCOUNTS_ENDPOINT = '/trim/accounts';

const buildAccountsUrl = (appName?: string): string => {
  const queryString = new URLSearchParams();
  if (appName) {
    queryString.set('appName', appName);
  }
  try {
    const { pathname } = window.location;
    if (pathname?.startsWith('/')) {
      queryString.set('pageUrl', pathname);
    }
  } catch {
    // Preserve the existing request when location is unavailable.
  }
  const serializedQueryString = queryString.toString();
  return `${ACCOUNTS_ENDPOINT}${
    serializedQueryString ? `?${serializedQueryString}` : ''
  }`;
};

/**
 * A request carries a `query` OR a `nextToken`, never both (the backend rejects
 * both with a 400). First page: send `query` (present = search, absent =
 * browse). Subsequent pages: send only the `nextToken` from the prior response,
 * which already carries the query — the client never re-sends it.
 */
export interface AccountPageParams {
  query?: string;
  nextToken?: string | null;
  appName?: string;
}

/**
 * POST /trim/accounts — one page of the whale account switcher. Browse when no
 * `query`/`nextToken`; search when `query` is present; paginate when `nextToken`
 * is present. Unlike the page-load endpoints, this propagates ACVS errors
 * (throws) rather than degrading to an empty list, so the caller can retry.
 */
export const fetchAccountsPage = async (
  { query, nextToken, appName }: AccountPageParams,
  signal?: AbortSignal,
): Promise<AccountPageResponse> => {
  const url = buildAccountsUrl(appName);
  // Send query XOR nextToken — omit the absent one entirely so a browse request
  // never carries an empty query and a paginated request never resends one.
  const body = nextToken ? { nextToken } : query ? { query } : {};
  const response = await request<AccountPageResponse>({
    url,
    method: 'POST',
    data: body,
    ...(signal && { signal }),
  });
  return response.data;
};
